import { ArrowLeft, ChevronDown, ChevronRight, ExternalLink, RefreshCw, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { api } from '../api/client';
import type {
  QueueJob,
  QueueJobSummary,
  QueueJobsPayload,
  QueueSearchScope,
  QueueState,
  QueuesPayload,
} from '../api/types';
import { LoadIssue } from '../components/LoadIssue';
import { ScreenLink } from '../components/ScreenLink';
import { duration } from '../lib/format';
import { STATE_META, boardQueueUrl, toState } from '../lib/queueStates';
import { readParams, withParam, writeUrl } from '../lib/urlState';
import { useLoad } from '../lib/useLoad';

const LIMIT = 50;

const relative = (ms: number | null): string => {
  if (ms === null) return '—';
  const now = Date.now();
  return ms > now ? `in ${duration(ms - now)}` : `${duration(now - ms)} ago`;
};

/** The one timestamp that matters for this state, phrased for it. */
const when = (job: QueueJob, state: QueueState): string => {
  if (state === 'delayed') return job.runsAt !== null ? `due ${relative(job.runsAt)}` : relative(job.timestamp);
  if (state === 'failed') return `failed ${relative(job.finishedOn ?? job.timestamp)}`;
  if (state === 'completed') return `done ${relative(job.finishedOn ?? job.timestamp)}`;
  if (state === 'active') return job.processedOn !== null ? `started ${relative(job.processedOn)}` : relative(job.timestamp);
  return `queued ${relative(job.timestamp)}`;
};

const stamp = (ms: number | null) => (ms === null ? '—' : new Date(ms).toLocaleString());

/** BullMQ's job name as a reader says it: `prepare-job-email` is "Prepare job email". */
const jobLabel = (job: QueueJob): string => {
  const name = job.name && job.name !== '__default__' ? job.name : 'job';
  const words = name.replace(/[-_]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** What a search result matched on, as the chip beside it says it. */
const MATCHED: Record<string, string> = {
  id: 'queue id',
  job: 'job id',
  chauffeur: 'chauffeur id',
  customer: 'customer id',
  affiliate: 'affiliate id',
  to: 'recipient',
  template: 'template',
};

const ID_LABELS: [keyof QueueJobSummary['ids'], string][] = [
  ['job', 'job'],
  ['chauffeur', 'chauffeur'],
  ['customer', 'customer'],
  ['affiliate', 'affiliate'],
];

/** The route's own entry: the queue is in the path, `/queues/dispatch-be/email`. */
export function QueueJobsRoute() {
  const { system = '', queue = '' } = useParams();
  return <QueueJobs system={system} queue={queue} />;
}

/**
 * Inside one queue: the jobs in one state, a page at a time, with a search
 * that understands ids. A page of its own rather than the drawer it used to
 * be -- the rows carry a template, a category and recipients now, and a drawer
 * as wide as those need covered the queue list it was meant to sit beside.
 *
 * The tab, the search and its scope are the URL's, so "the failed emails for
 * job 1234" is a link somebody can paste into a chat.
 *
 * Reads only. Retry, promote and remove stay in Bull Board, which is linked at
 * the top on the same queue and state -- the console's line is observe-only,
 * and this page is where that line pays off: the failed job's reason and
 * payload are read HERE, and the button that would resend it is one click away
 * behind Bull Board's own login rather than behind this console's.
 */
export function QueueJobs({ system, queue }: { system: string; queue: string }) {
  const [state, setState] = useState<QueueState>(() => toState(readParams().get('state') ?? 'waiting'));
  const [search, setSearch] = useState(() => readParams().get('q') ?? '');
  const [applied, setApplied] = useState(() => (readParams().get('q') ?? '').trim());
  // Every state by default: whoever asks "what happened to job 1234's email"
  // does not know yet whether it went, failed or is still waiting.
  const [scope, setScope] = useState<QueueSearchScope>(() => (readParams().get('scope') === 'state' ? 'state' : 'all'));
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    let params = withParam(readParams(), 'state', state);
    params = withParam(params, 'q', applied || null);
    params = withParam(params, 'scope', applied && scope === 'state' ? 'state' : null);
    writeUrl(params);
  }, [state, applied, scope]);

  // The queue's own row, for the tab counts, the incidents and Bull Board's
  // address: the Queues screen's read, on the same clock.
  const overview = useLoad(() => api.get<QueuesPayload>('/queues'), [], 10_000);
  const systemRow = overview.data?.systems.find((entry) => entry.system === system);
  const row = systemRow?.queues.find((entry) => entry.name === queue) ?? null;

  const { data, error, reload, refreshing } = useLoad(
    () =>
      api.get<QueueJobsPayload>(
        `/queues/${encodeURIComponent(system)}/${encodeURIComponent(queue)}/jobs?state=${state}&limit=${LIMIT}&offset=${offset}${
          applied ? `&q=${encodeURIComponent(applied)}&scope=${scope}` : ''
        }`,
      ),
    [system, queue, state, offset, applied, scope],
  );

  // A page from a different tab or query is not this page: hold the frame, but
  // do not let the old rows read as the new state's contents.
  const current =
    data &&
    data.state === state &&
    data.queue === queue &&
    (data.search?.q ?? '').toLowerCase() === applied.toLowerCase() &&
    (!applied || data.search?.scope === scope)
      ? data
      : null;
  const everyState = Boolean(current?.search && current.search.scope === 'all');

  const meta = STATE_META.find((entry) => entry.key === state) ?? STATE_META[0];
  const boardUrl = boardQueueUrl(overview.data?.bullBoards[system], queue, state);
  const total = current?.total ?? (row ? meta.count(row) : 0);
  const from = current ? current.offset + 1 : offset + 1;
  const to = current ? current.offset + current.jobs.length : Math.min(offset + LIMIT, total);

  const pickState = (next: QueueState) => {
    setOffset(0);
    setExpanded(null);
    setState(next);
  };

  return (
    <>
      <div className="wt-head wt-queue-head">
        <h1>{queue}</h1>
        <span className="sub">
          {system}
          {row && (
            <>
              {' '}
              · {row.waiting.toLocaleString()} waiting · {row.active} active · {row.delayed} delayed · {row.failed} failed
              {row.paused && ' · PAUSED'}
            </>
          )}
        </span>
        <span className="wt-spacer" />
        <button
          type="button"
          className="btn btn-ghost btn-icon"
          title="Re-read"
          aria-label="Re-read"
          onClick={() => {
            void reload();
            void overview.reload();
          }}
        >
          <RefreshCw size={14} className={refreshing ? 'wt-spin' : ''} />
        </button>
        {boardUrl && (
          <a className="btn btn-secondary" href={boardUrl} target="_blank" rel="noreferrer" title="Open this queue and state in Bull Board, where the actions are">
            Bull Board <ExternalLink size={13} />
          </a>
        )}
        <ScreenLink to="/queues" previewTab="queues" className="btn btn-secondary">
          <ArrowLeft size={14} /> Queues
        </ScreenLink>
      </div>

      <LoadIssue error={overview.error} updatedAt={overview.updatedAt} what="queues" onRetry={() => void overview.reload()} />
      {systemRow && !systemRow.reachable && (
        <div className="wt-banner wt-banner-warn">
          {system} is not reachable: {systemRow.reason}. Nothing here is being watched.
        </div>
      )}
      {overview.data && systemRow?.reachable && !row && (
        <div className="wt-banner wt-banner-warn">
          {system} has no queue called “{queue}” that sphere-watch watches.
        </div>
      )}

      {row?.incidents.map((incident) => (
        <div key={incident.id} className={`wt-banner ${incident.severity === 'warning' || incident.severity === 'info' ? 'wt-banner-warn' : 'wt-banner-danger'}`}>
          <strong>{incident.summary ?? incident.checkId}</strong>
          {incident.remedy && <div className="wt-remedy">{incident.remedy}</div>}
        </div>
      ))}

      <nav className="wt-tabs" aria-label="Job state">
        {STATE_META.map((entry) => (
          <button
            key={entry.key}
            type="button"
            className={entry.key === state ? 'active' : ''}
            aria-pressed={entry.key === state}
            onClick={() => pickState(entry.key)}
          >
            {entry.label} {row && <b>{entry.count(row).toLocaleString()}</b>}
          </button>
        ))}
      </nav>
      <p className="wt-tab-help">{meta.help}</p>

      <div className="wt-queue-tools">
        <label className="wt-search">
          <Search size={14} />
          <input
            className="input"
            placeholder="job 1234, chauffeur 45, to:ann@…, template:QUOTE, or any text"
            aria-label="Search jobs: a number is an exact id; job, chauffeur, customer, affiliate, to and template narrow it"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                setOffset(0);
                setApplied(search.trim());
              }
            }}
          />
        </label>
        <select
          className="select wt-search-scope"
          aria-label="Where to search"
          value={scope}
          onChange={(event) => {
            setScope(event.target.value as QueueSearchScope);
            setOffset(0);
          }}
        >
          <option value="all">in every state</option>
          <option value="state">in {meta.label} only</option>
        </select>
        {applied && (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              setSearch('');
              setApplied('');
              setOffset(0);
            }}
          >
            Clear
          </button>
        )}
        <span className="wt-spacer" />
        {!applied && total > 0 && (
          <span className="wt-pager">
            <button type="button" className="btn btn-ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))}>
              ‹
            </button>
            <span className="wt-mono">
              {from}–{Math.max(from, to)} of {total.toLocaleString()}
            </span>
            <button type="button" className="btn btn-ghost" disabled={offset + LIMIT >= total} onClick={() => setOffset(offset + LIMIT)}>
              ›
            </button>
          </span>
        )}
      </div>

      {current?.search && (
        <p className="wt-search-note">
          {current.search.matched} match{current.search.matched === 1 ? '' : 'es'} for “{current.search.q}”
          {current.search.kind.startsWith('id:') && ' (an exact id)'} among the first{' '}
          {current.search.scanned.toLocaleString()} {everyState ? 'jobs in every state' : `${meta.label.toLowerCase()} jobs`}
          {current.search.truncated && (
            <>
              {' '}
              — <strong>{(current.total - current.search.scanned).toLocaleString()} more were not searched</strong>; narrow the state or use Bull Board
            </>
          )}
          .
        </p>
      )}

      {error ? (
        <div className="wt-banner wt-banner-danger">{error.message}</div>
      ) : !current ? (
        <div className="wt-empty">Reading…</div>
      ) : current.jobs.length === 0 ? (
        <div className="wt-empty">
          {applied ? 'Nothing matched.' : `Nothing ${state === 'completed' ? 'kept as done' : state} right now.`}
        </div>
      ) : (
        <JobTable
          jobs={current.jobs}
          state={state}
          everyState={everyState}
          expanded={expanded}
          onToggle={(id) => setExpanded(expanded === id ? null : id)}
        />
      )}

      <p className="wt-drawer-foot">
        This page only reads. Payloads are shown with credential-shaped fields redacted and long ones cut short; retry,
        promote and remove live in Bull Board behind its own login.
      </p>
    </>
  );
}

interface Columns {
  template: boolean;
  category: boolean;
  recipients: boolean;
  priority: boolean;
}

/**
 * The jobs of one page or one search. Template, Category and Sent to appear
 * only when some job on the page has them -- a location-ping queue has none of
 * the three, and an empty column on every row says nothing. In an every-state
 * search the rows come from different tabs, so each says which it is in.
 */
function JobTable({
  jobs,
  state,
  everyState,
  expanded,
  onToggle,
}: {
  jobs: QueueJob[];
  state: QueueState;
  everyState: boolean;
  expanded: string | null;
  onToggle: (id: string) => void;
}) {
  const columns: Columns = {
    template: jobs.some((job) => job.summary?.template || job.summary?.subject),
    category: jobs.some((job) => job.summary?.category),
    recipients: jobs.some((job) => job.summary?.recipients),
    priority: !everyState && state === 'prioritized',
  };
  return (
    <div className="wt-table-wrap">
      <table className="wt-table wt-jobs wt-stack">
        <thead>
          <tr>
            <th style={{ width: 28 }} />
            <th>Job</th>
            {columns.template && <th>Template</th>}
            {columns.category && <th>Category</th>}
            {columns.recipients && <th>Sent to</th>}
            <th>When</th>
            <th style={{ width: 60, textAlign: 'right' }}>Tries</th>
            {columns.priority && <th style={{ width: 70, textAlign: 'right' }}>Priority</th>}
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => (
            <JobRows
              key={`${job.state}:${job.id}`}
              job={job}
              columns={columns}
              everyState={everyState}
              open={expanded === job.id}
              onToggle={() => onToggle(job.id)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Recipients({ summary }: { summary: QueueJobSummary | null }) {
  const recipients = summary?.recipients;
  if (!recipients) return <span className="wt-dim">—</span>;
  if (!recipients.to.length) return <span className="wt-dim wt-job-note">{recipients.note ?? '—'}</span>;
  const more = recipients.total - 1;
  const copies = [recipients.cc ? `cc ${recipients.cc}` : null, recipients.bcc ? `bcc ${recipients.bcc}` : null].filter(Boolean);
  return (
    <span title={recipients.to.join('\n')}>
      <span className="wt-job-to">{recipients.to[0]}</span>
      {(more > 0 || copies.length > 0) && (
        <span className="wt-job-sub">{[more > 0 ? `+${more} more` : null, ...copies].filter(Boolean).join(' · ')}</span>
      )}
    </span>
  );
}

function Template({ summary }: { summary: QueueJobSummary | null }) {
  if (!summary?.template && !summary?.subject) return <span className="wt-dim">—</span>;
  return (
    <>
      {summary.template ? (
        <span className="wt-job-template">{summary.template}</span>
      ) : (
        <span className="wt-dim wt-job-note" title="Built in code, not a saved template, so it has no name">
          no saved template
        </span>
      )}
      {summary.subject && <span className="wt-job-sub">{summary.subject}</span>}
    </>
  );
}

function JobRows({
  job,
  columns,
  everyState,
  open,
  onToggle,
}: {
  job: QueueJob;
  columns: Columns;
  everyState: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  const summary = job.summary;
  const span = 4 + Object.values(columns).filter(Boolean).length;
  const jobState = job.state as QueueState;
  return (
    <>
      <tr
        className="wt-row-click"
        onClick={onToggle}
        tabIndex={0}
        aria-expanded={open}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggle();
          }
        }}
      >
        <td className="wt-dim wt-stack-hide">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
        <td className="wt-stack-wide" data-label="Job">
          <div className="wt-job-title">{jobLabel(job)}</div>
          <div className="wt-job-sub">
            <span className="wt-mono">#{job.id}</span>
            {summary?.tenant && <> · {summary.tenant}</>}
            {everyState && <span className={`wt-chip${jobState === 'failed' ? ' wt-chip-failed' : ''}`}>{jobState}</span>}
            {summary?.flags.map((flag) => (
              <span key={flag} className="wt-chip">
                {flag}
              </span>
            ))}
            {job.matched && MATCHED[job.matched] && (
              <span className="wt-chip wt-chip-ack" title="What the search matched">
                {MATCHED[job.matched]}
              </span>
            )}
            {job.redacted && (
              <span className="wt-chip wt-chip-shadow" title="Credential-shaped fields in the payload were replaced">
                redacted
              </span>
            )}
          </div>
          {job.state === 'failed' && job.failedReason && <div className="wt-job-reason">{job.failedReason}</div>}
        </td>
        {columns.template && (
          <td className="wt-stack-wide" data-label="Template">
            <Template summary={summary} />
          </td>
        )}
        {columns.category && (
          <td data-label="Category">
            {summary?.category ? (
              <>
                {summary.category}
                {summary.subjectId && <span className="wt-mono wt-dim"> #{summary.subjectId}</span>}
              </>
            ) : (
              <span className="wt-dim">—</span>
            )}
          </td>
        )}
        {columns.recipients && (
          <td className="wt-job-recipients" data-label="Sent to">
            <Recipients summary={summary} />
          </td>
        )}
        <td className="wt-dim wt-job-when" data-label="When">
          {when(job, jobState)}
        </td>
        <td className="wt-mono wt-num" data-label="Tries">
          {job.attemptsMade}
        </td>
        {columns.priority && (
          <td className="wt-mono wt-num" data-label="Priority">
            {job.priority ?? '—'}
          </td>
        )}
      </tr>
      {open && (
        <tr className="wt-job-detail">
          <td colSpan={span} className="wt-stack-wide">
            {summary && (
              <div className="wt-audit-meta">
                {summary.template && (
                  <span>
                    <b>template</b>
                    <code>{summary.template}</code>
                  </span>
                )}
                {summary.subject && (
                  <span>
                    <b>subject</b>
                    <code>{summary.subject}</code>
                  </span>
                )}
                {summary.tenant && (
                  <span>
                    <b>tenant</b>
                    <code>{summary.tenant}</code>
                  </span>
                )}
                {ID_LABELS.filter(([key]) => summary.ids[key].length).map(([key, label]) => (
                  <span key={key}>
                    <b>{label}</b>
                    <code>{summary.ids[key].map((id) => `#${id}`).join(', ')}</code>
                  </span>
                ))}
                {summary.recipients && summary.recipients.to.length > 0 && (
                  <span>
                    <b>to</b>
                    <code>
                      {summary.recipients.to.join(', ')}
                      {summary.recipients.total > summary.recipients.to.length &&
                        ` and ${summary.recipients.total - summary.recipients.to.length} more`}
                    </code>
                  </span>
                )}
                {summary.recipients && (summary.recipients.cc > 0 || summary.recipients.bcc > 0) && (
                  <span>
                    <b>copies</b>
                    <code>
                      cc {summary.recipients.cc} · bcc {summary.recipients.bcc}
                    </code>
                  </span>
                )}
              </div>
            )}
            <div className="wt-audit-meta">
              <span>
                <b>queued</b>
                <code>{stamp(job.timestamp)}</code>
              </span>
              <span>
                <b>started</b>
                <code>{stamp(job.processedOn)}</code>
              </span>
              <span>
                <b>finished</b>
                <code>{stamp(job.finishedOn)}</code>
              </span>
              {job.runsAt !== null && (
                <span>
                  <b>due</b>
                  <code>{stamp(job.runsAt)}</code>
                </span>
              )}
              {job.priority !== null && (
                <span>
                  <b>priority</b>
                  <code>{job.priority}</code>
                </span>
              )}
              {job.processedBy && (
                <span>
                  <b>worker</b>
                  <code>{job.processedBy}</code>
                </span>
              )}
            </div>
            {job.failedReason && (
              <div className="wt-audit-error">
                <div className="title">Failed: {job.failedReason}</div>
                {job.stacktrace && <pre>{job.stacktrace}</pre>}
              </div>
            )}
            <div className="wt-audit-payload-head">
              <b>Payload</b>
              {job.dataTruncated && <span className="wt-chip">cut short</span>}
            </div>
            <pre className="wt-audit-payload">{job.dataPreview ?? '(none)'}</pre>
            {job.optsPreview && (
              <details className="wt-job-opts">
                <summary>Options</summary>
                <pre className="wt-audit-payload">{job.optsPreview}</pre>
              </details>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
